<script>
import Layout from '@/views/layouts/main';
import PageHeader from '@/components/page-header';
import AccountsPayable from '@/controller/AccountsPayable';
import { authEasyMethods } from '@/state/helpers';
import MultiSelect from 'vue-multiselect';

export default {
  components: { Layout, PageHeader, MultiSelect },
  data() {
    return {
        filters: {
            iniDate: null,
            endDate: null,
            iniDatePagamento: null,
            endDatePagamento: null,
            competenciaMes: null,
            competenciaAno: null,
            status: null,
            category: [],
        },
        statusOptions: [
            { value: null, text: 'Todos' },
            { value: 0, text: 'Pendentes' },
            { value: 1, text: 'Pagos' },
        ],
        categoryOptions: [],
        planoContas: [],
        // Visualização de PDF
        pdfSrc: null,
        isLoadingPdf: false,
        pdfError: null,
        // UI
        showFilters: true,
        currentEmpresa: null,
        competenciaMesOptions: [
            { value: null, text: 'Todos os meses' },
            { value: 1, text: 'Janeiro' },
            { value: 2, text: 'Fevereiro' },
            { value: 3, text: 'Março' },
            { value: 4, text: 'Abril' },
            { value: 5, text: 'Maio' },
            { value: 6, text: 'Junho' },
            { value: 7, text: 'Julho' },
            { value: 8, text: 'Agosto' },
            { value: 9, text: 'Setembro' },
            { value: 10, text: 'Outubro' },
            { value: 11, text: 'Novembro' },
            { value: 12, text: 'Dezembro' },
        ],
        competenciaAnoOptions: [
            { value: null, text: 'Todos os anos' },
            { value: 2023, text: '2023' },
            { value: 2024, text: '2024' },
            { value: 2025, text: '2025' },
            { value: 2026, text: '2026' },
        ],
    };
  },
  computed: {
    ...authEasyMethods,
    buttonText() {
      return this.showFilters ? 'Gerar relatório' : 'Filtros...';
    }
  },
  created() {
    this.currentEmpresa = JSON.parse(localStorage.getItem('currentEmpresa'));
    this.accountsPayable = new AccountsPayable();
    this.loadPlanoContas();
  },
  mounted() {
    this.filters.iniDate = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0];
    this.filters.endDate = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0];
    // Campos de pagamento iniciam com NULL
    this.filters.iniDatePagamento = null;
    this.filters.endDatePagamento = null;
    // Competência mês inicia com mês anterior ao atual
    this.filters.competenciaMes = new Date().getMonth() === 0 ? 12 : new Date().getMonth();
    // Competência ano inicia com ano atual
    this.filters.competenciaAno = new Date().getFullYear();
  },
  watch: {
    'filters.status'(newVal) {
      if (newVal === 0) {
        this.filters.iniDatePagamento = null;
        this.filters.endDatePagamento = null;
      }
    }
  },
  beforeDestroy() {
    if (this.pdfSrc && this.pdfSrc.startsWith('blob:')) {
      URL.revokeObjectURL(this.pdfSrc);
    }
  },
  methods: {
    onPrimaryButtonClick() {
      if (!this.showFilters) {
        // Mostrar filtros e ocultar PDF
        this.showFilters = true;
        this.clearPdf();
        this.isLoadingPdf = false;
        this.pdfError = null;
        return;
      }
      // Caso filtros estejam visíveis, gerar relatório
      this.generateReport();
    },
    async generateReport() {
      try {
        this.pdfError = null;
        this.isLoadingPdf = true;
        this.clearPdf();

        const categorias = Array.isArray(this.filters.category)
          ? this.filters.category
              .map(opt => (typeof opt === 'object' ? opt.value : opt))
              .filter(v => typeof v === 'number')
          : [];

        const payload = {
          empresa_id: this.currentEmpresa?.id,
          vencimento_de: this.filters.iniDate || null,
          vencimento_ate: this.filters.endDate || null,
          pagamento_de: this.filters.status === 0 ? null : (this.filters.iniDatePagamento || null),
          pagamento_ate: this.filters.status === 0 ? null : (this.filters.endDatePagamento || null),
          competencia_mes: this.filters.competenciaMes || null,
          competencia_ano: this.filters.competenciaAno || null,
          status: this.filters.status !== null ? this.filters.status : null,
          categorias,
        };

        // Chama a rota de relatório e espera um PDF (blob)
        const response = await this.accountsPayable.http.post(
          `/relatorios/contas-a-pagar/contas-pagas`,
          payload,
          { responseType: 'blob' }
        );

        const contentType = response.headers?.['content-type'] || response.headers?.get?.('content-type');
        if (contentType && contentType.includes('application/pdf')) {
          this.setPdfFromBlob(response.data);
          this.showFilters = false;
        } else {
          // Pode ter vindo um JSON de erro; tentar ler como texto
          this.pdfError = 'Não foi possível gerar o PDF do relatório.';
        }
      } catch (err) {
        this.pdfError = 'Erro ao gerar relatório. Verifique os filtros e tente novamente.';
      } finally {
        this.isLoadingPdf = false;
      }
    },
    formatCurrencyBR(value) {
      return this.formatterFloatBR(value, 2);
    },
    async loadPlanoContas() {
      if (!this.currentEmpresa || !this.currentEmpresa.id) {
        console.error('Empresa não encontrada');
        return;
      }
      
      try {
        const result = await this.accountsPayable.getAccountsPayable(this.currentEmpresa.id);
        if (result.success) {
          this.planoContas = result.data.planoContas;
          this.generateCategoryOptions();
        } else {
          console.error('Erro ao carregar plano de contas');
        }
      } catch (error) {
        console.error('Erro ao carregar plano de contas:', error);
      }
    },
    generateCategoryOptions() {
      // Filtra apenas as contas de despesa (conta_pgto === 1)
      const contasDespesas = this.planoContas.filter(conta => conta.conta_pgto === 1);
      
      // Gera as opções para o MultiSelect
      this.categoryOptions = [
        { value: null, text: 'Todas as categorias' },
        ...contasDespesas.map(conta => ({
          value: conta.id,
          text: conta.descricao
        }))
      ];
    },
    formatStatus(value) {
      return value === 0 ? 'Pendente' : 'Pago';
    },
    formatDateBR(value) {
      return this.formatterDateBR(value);
    },
    formatterFloat(value) {
      if (value === null || value === undefined || value === '') return '';
      const numeric = typeof value === 'number' ? value : parseFloat(String(value).replace(/\./g, '').replace(',', '.'));
      return this.formatterFloatBR(isNaN(numeric) ? 0 : numeric, 2);
    },
    // Helpers PDF
    setPdfFromBase64(base64String) {
      this.clearPdf();
      this.pdfSrc = `data:application/pdf;base64,${base64String}`;
    },
    setPdfFromBlob(blob) {
      this.clearPdf();
      this.pdfSrc = URL.createObjectURL(blob);
    },
    setPdfFromUrl(url) {
      this.clearPdf();
      this.pdfSrc = url;
    },
    clearPdf() {
      if (this.pdfSrc && this.pdfSrc.startsWith('blob:')) {
        URL.revokeObjectURL(this.pdfSrc);
      }
      this.pdfSrc = null;
      this.pdfError = null;
    }
  },
};
</script>

<template>
  <Layout>
    <PageHeader title="Relatório Contas a Pagar - Contas Pagas" />
    <div class="row" v-show="showFilters">
        <div class="col-12">
            <div class="card">
                <div class="card-body">
                    <b-card>
                        <div class="row mb-3">
                            <div class="col-3">
                                <b-form-group label="Vencimento de:">
                                    <b-form-input type="date" v-model="filters.iniDate"></b-form-input>
                                </b-form-group>
                            </div>
                            <div class="col-3">
                                <b-form-group label="Vencimento até:">
                                    <b-form-input type="date" v-model="filters.endDate"></b-form-input>
                                </b-form-group>
                            </div>
                            <div class="col-3">
                                <b-form-group label="Pagamento de:">
                                    <b-form-input type="date" v-model="filters.iniDatePagamento" :disabled="filters.status === 0"></b-form-input>
                                </b-form-group>
                            </div>
                            <div class="col-3">
                                <b-form-group label="Pagamento até:">
                                    <b-form-input type="date" v-model="filters.endDatePagamento" :disabled="filters.status === 0"></b-form-input>
                                </b-form-group>
                            </div>
                        </div>
                        <div class="row mb-3">
                            <div class="col-5">
                                <b-form-group label="Categoria:">
                                    <MultiSelect v-model="filters.category"
                                        :options="categoryOptions"
                                        :multiple="true"
                                        :close-on-select="false"
                                        :clear-on-select="false"
                                        :hide-selected="false"
                                        :select-label="'Selecionar'"
                                        :deselect-label="'Remover'"
                                        :remove-selected="true"
                                        track-by="value"
                                        label="text"
                                    >
                                        <template #option="{ option }">
                                            <span>{{ option.text }}</span>
                                        </template>
                                        <template #selected="{ selected }">
                                            <span>{{ selected.text }}</span>
                                        </template>
                                    </MultiSelect>
                                </b-form-group>
                            </div>
                            <div class="col-2">
                                <b-form-group label="Competência Mês:">
                                    <b-form-select v-model="filters.competenciaMes" :options="competenciaMesOptions"></b-form-select>
                                </b-form-group>
                            </div>
                            <div class="col-2">
                                <b-form-group label="Competência Ano:">
                                    <b-form-select v-model="filters.competenciaAno" :options="competenciaAnoOptions"></b-form-select>
                                </b-form-group>
                            </div>
                            <div class="col-3">
                                <b-form-group label="Situação:">
                                    <b-form-radio-group v-model="filters.status" :options="statusOptions"></b-form-radio-group>
                                </b-form-group>
                            </div>
                        </div>
                    </b-card>
                </div>
            </div>
        </div>
    </div>
    <!-- Botão principal sempre visível -->
    <div class="row">
      <div class="col-12 text-right mb-2">
        <b-button variant="primary" @click="onPrimaryButtonClick">{{ buttonText }}</b-button>
      </div>
    </div>
    <!-- Área de visualização do PDF -->
    <div class="row mt-3">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div v-if="isLoadingPdf" class="text-center py-5">
              <b-spinner label="Carregando..."></b-spinner>
              <div class="text-muted mt-2">Gerando relatório em PDF...</div>
            </div>
            <b-alert v-if="pdfError" variant="danger" show class="mb-3">{{ pdfError }}</b-alert>
            <div v-if="pdfSrc" class="pdf-container">
              <iframe :src="pdfSrc" width="100%" height="820" style="border: none;"></iframe>
            </div>
            <div v-else-if="!isLoadingPdf && showFilters" class="text-center text-muted py-5">
              Selecione os filtros e clique em "Gerar relatório" para visualizar o PDF aqui.
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.pdf-container {
  width: 100%;
  min-height: 820px;
}
</style>
